import React, { useReducer, useCallback, useRef, useEffect, Fragment, useState } from 'react';
import { KeyboardAvoidingView, View, Text, TextInput } from 'react-native';
import PropTypes from 'prop-types';
import { t } from 'Locale';
import { Device, hideBars } from 'DeviceUtils';
import { noop } from 'Utils';
import { ActivityIndicator, Hr, Link } from 'LocalGluons';
import { MAX_PIN_TRIES, PIN_LENGTH } from '../../../options';

import defaultStyles from './PinInput.jss';
import { TopKeyboardAlert } from './components';
import useBlockedHandler from './useBlockedHandler';

const ACTIONS = Object.freeze({
    addValue: 'addValue',
    delete: 'delete',
    pinInvalid: 'pinInvalid',
    clearAll: 'clearAll',
    changeLoading: 'changeLoading',
    removePinInvalidMessage: 'removePinInvalidMessage',
    pinBlocked: 'pinBlocked'
});

const initState = {
    currentIndex: 0,
    previousIndex: 0,
    pinArray: [],
    invalidPinMessage: null,
    pinBlocked: false,
    loading: false
};

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.addValue:
            if (action.value) {
                return {
                    invalidPinMessage: null,
                    currentIndex: state.currentIndex + 1,
                    previousIndex: state.currentIndex,
                    pinArray: [...state.pinArray, action.value]
                };
            }
            break;
        case ACTIONS.pinInvalid:
            if (action.value) {
                return {
                    ...initState,
                    invalidPinMessage: action.value
                };
            }
            break;
        case ACTIONS.pinBlocked:
            if (action.value) {
                return {
                    ...initState,
                    pinBlocked: action.value
                };
            }
            break;
        case ACTIONS.removePinInvalidMessage:
            return {
                ...state,
                invalidPinMessage: null
            };
        case ACTIONS.clearAll:
            return {
                ...initState
            };
        case ACTIONS.changeLoading:
            return {
                ...state,
                loading: action.value
            };
        case ACTIONS.delete:
            return {
                ...state,
                invalidPinMessage: null,
                currentIndex: state.currentIndex > 0 ? state.currentIndex - 1 : 0,
                previousIndex: state.currentIndex,
                pinArray: state.pinArray.slice(0, -1)
            };
        default:
            break;
    }
    return state;
}

const PinInput = ({
    header,
    message,
    onForgotPinPressed,
    isDefaultPinShown,
    onPinSuccess,
    onPinFailed,
    pinValidateFunction,
    invalidPinMessage,
    cleanErrorMessage,
    fromPlayer
}) => {
    const [state, dispatch] = useReducer(reducer, initState);
    const [tryCount, setTryCount] = useState(1);
    const { isPinBlocked, setPinBlocked } = useBlockedHandler();

    const itemsRef = useRef([]);

    useEffect(() => {
        if (invalidPinMessage && !state.invalidPinMessage) {
            dispatch({ type: ACTIONS.pinInvalid, value: invalidPinMessage });
        }
    }, [invalidPinMessage]);

    const onKeyPressCallback = useCallback(({ nativeEvent }) => {
        if (nativeEvent.key === 'Backspace') {
            dispatch({ type: ACTIONS.delete });
        }
    }, []);

    const onChangeTextCallback = useCallback((value) => {
        if (/^\d+$/.test(value)) {
            dispatch({ type: ACTIONS.addValue, value });
        }
    }, []);

    const onForgotPinCallback = useCallback(() => {
        onForgotPinPressed();
    }, []);

    const closeTopKeyboardAlertCallback = useCallback(() => {
        dispatch({ type: ACTIONS.removePinInvalidMessage });
    }, []);

    const onError = (error) => {
        if (error.noInternet) {
            dispatch({ type: ACTIONS.pinInvalid, value: error.errorMessage });
            return;
        }
        if (typeof onPinFailed === 'function') {
            onPinFailed(error.localizedMessage || error.errorMessage);
            return;
        }
        if (tryCount < MAX_PIN_TRIES) {
            dispatch({ type: ACTIONS.pinInvalid, value: error.errorMessage });
            setTryCount(tryCount + 1);
            return;
        }
        setPinBlocked();
        dispatch({ type: ACTIONS.pinBlocked, value: true });
        setTryCount(1);
    };

    useEffect(() => {
        if (state.pinBlocked !== isPinBlocked)
            dispatch({ type: ACTIONS.pinBlocked, value: isPinBlocked });
    }, [isPinBlocked]);

    useEffect(() => {
        const onCurrentIndexMove = async () => {
            if (state.currentIndex < PIN_LENGTH) {
                // If there is another pin input -> focus it
                if (state.currentIndex || state.invalidPinMessage || state.previousIndex) {
                    itemsRef.current[state.currentIndex].focus();
                }
            } else {
                if (cleanErrorMessage) cleanErrorMessage();
                // The whole pin was provided
                dispatch({ type: ACTIONS.changeLoading, value: true });

                // Blur keyboard on the last pin input
                itemsRef.current[state.currentIndex - 1].blur();
                try {
                    // Send request to a server
                    const error = (await pinValidateFunction(state.pinArray.join(''))) || {};
                    dispatch({ type: ACTIONS.changeLoading, value: false });

                    if (error.errorMessage) {
                        onError(error);
                    } else {
                        // Ensure that bars are still hidden
                        if (fromPlayer) hideBars();
                        // Pin was successful
                        onPinSuccess();
                    }
                } catch (error) {
                    onError(error);
                }
            }
        };

        onCurrentIndexMove();
    }, [state.currentIndex]);

    useEffect(() => {
        const waitForFocusTimeout = setTimeout(() => {
            if (itemsRef.current[0]) itemsRef.current[0].focus();
        }, 100);
        return () => clearTimeout(waitForFocusTimeout);
    }, []);

    const renderPinInputs = () => {
        const codeInputs = [];

        for (let i = 0; i < PIN_LENGTH; i++) {
            codeInputs.push(
                <TextInput
                    key={i}
                    ref={(el) => {
                        itemsRef.current[i] = el;
                    }}
                    style={[
                        defaultStyles.pinInput,
                        fromPlayer && Device.isAndroid
                            ? {
                                  width: 40,
                                  height: 40,
                                  fontSize: 20
                              }
                            : {},
                        Device.isWeb && defaultStyles.webPinInput
                    ]}
                    maxLength={1}
                    keyboardType="numeric"
                    value={state.pinArray[i] ? state.pinArray[i].toString() : ''}
                    onChangeText={onChangeTextCallback}
                    onKeyPress={onKeyPressCallback}
                    disableFullscreenUI
                    secureTextEntry={!Device.isWeb}
                    saveFormDataDisabled
                    autoCompleteType="off"
                    autocomplete="off"
                    testID={`pin_input_${i}`}
                />
            );
        }

        return codeInputs;
    };

    return (
        <>
            <KeyboardAvoidingView
                style={defaultStyles.container}
                behavior={Device.isIos || fromPlayer ? 'padding' : null}
            >
                {header && (
                    <Text
                        style={[
                            defaultStyles.title,
                            fromPlayer && Device.isAndroid
                                ? {
                                      fontSize: 20
                                  }
                                : {}
                        ]}
                    >
                        {isPinBlocked ? t('SETTINGS.PARENTAL_PIN_BLOCKED_TITLE') : header}
                    </Text>
                )}
                <Hr />
                {message && (
                    <Text
                        style={[
                            defaultStyles.message,
                            fromPlayer
                                ? {
                                      marginTop: Device.isAndroid ? 10 : 15,
                                      fontSize: Device.isAndroid ? 16 : 20
                                  }
                                : {}
                        ]}
                        numberOfLines={Device.isWeb ? 1 : 4}
                    >
                        {isPinBlocked ? t('SETTINGS.PARENTAL_PIN_BLOCKED_DESCRIPTION') : message}
                    </Text>
                )}
                {!isPinBlocked && (
                    <View
                        style={[
                            defaultStyles.pinInputWrapper,
                            fromPlayer
                                ? {
                                      marginVertical: Device.isAndroid ? 10 : 15
                                  }
                                : {}
                        ]}
                    >
                        {renderPinInputs()}
                    </View>
                )}

                {onForgotPinPressed && (
                    <Link
                        label={t('SETTINGS.PARENTAL_FORGOT_PIN')}
                        onPress={onForgotPinCallback}
                        style={defaultStyles.forgotPin}
                        testID="forgotten_pin"
                    />
                )}
                {isDefaultPinShown && (
                    <Text style={defaultStyles.defaultPinMessage} numberOfLines={2}>
                        {t('SETTINGS.PARENTAL_CONTROL_ENTER_PARENTAL_PIN_INFO')}
                    </Text>
                )}
            </KeyboardAvoidingView>
            {state.invalidPinMessage && (
                <TopKeyboardAlert
                    message={state.invalidPinMessage}
                    style={defaultStyles.alert}
                    onClose={closeTopKeyboardAlertCallback}
                />
            )}
            {state.loading && <ActivityIndicator isOverlay />}
        </>
    );
};

PinInput.defaultProps = {
    message: null,
    isDefaultPinShown: false,
    onForgotPinPressed: noop,
    onPinFailed: null,
    invalidPinMessage: null,
    fromPlayer: false
};

PinInput.propTypes = {
    header: PropTypes.string.isRequired,
    message: PropTypes.string,
    isDefaultPinShown: PropTypes.bool,
    onPinSuccess: PropTypes.func.isRequired,
    onPinFailed: PropTypes.func,
    onForgotPinPressed: PropTypes.func,
    pinValidateFunction: PropTypes.func.isRequired,
    invalidPinMessage: PropTypes.string,
    fromPlayer: PropTypes.bool
};

export { PinInput };
